import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, MapPin } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Velg baner med kapasitetsstyring + antall standplasser (endrer aldri QR-koden)
const Row = ({ orgId, loc, onSaved }) => {
  const [on, setOn] = useState(!!loc.shooting_positions);
  const [val, setVal] = useState(loc.shooting_positions || '');
  const [busy, setBusy] = useState(false);

  const save = async (positions) => {
    setBusy(true);
    try {
      const r = await axios.patch(`${API_BASE}/api/organizations/${orgId}/locations/${loc.id}/capacity`,
        { shooting_positions: positions }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      onSaved(loc.id, r.data.shooting_positions);
      toast.success(positions ? `${loc.name}: ${positions} standplasser` : `${loc.name}: kapasitetsstyring av`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunne ikke lagre');
    } finally { setBusy(false); }
  };

  const toggle = (checked) => {
    setOn(checked);
    if (!checked) { setVal(''); save(0); } else if (Number(val) > 0) save(Number(val));
  };
  const commit = () => { const n = Number(val); if (on && n > 0 && n !== loc.shooting_positions) save(n); };

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 p-3 rounded-lg bg-gray-800/60 border border-gray-700" data-testid={`capacity-location-${loc.id}`}>
      <label className="flex items-center gap-3 flex-1 min-w-[180px] cursor-pointer select-none">
        <input type="checkbox" checked={on} onChange={(e) => toggle(e.target.checked)} disabled={busy}
          className="w-5 h-5 accent-yellow-500 cursor-pointer shrink-0" data-testid={`capacity-location-toggle-${loc.id}`} />
        <MapPin className="w-4 h-4 text-gray-400 shrink-0" />
        <span className="text-white text-sm font-medium break-words min-w-0">{loc.name}</span>
      </label>
      {on && (
        <div className="flex items-center gap-2 ml-8 sm:ml-0">
          <input type="number" inputMode="numeric" min="1" max="200" value={val} disabled={busy}
            onChange={(e) => setVal(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }}
            placeholder="Antall" autoFocus={!loc.shooting_positions}
            className="w-20 px-2 h-9 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
            data-testid={`capacity-location-positions-${loc.id}`} />
          <span className="text-xs text-gray-400 whitespace-nowrap">standplasser</span>
          {busy && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
        </div>
      )}
    </div>
  );
};

export const CapacityLocationsSettings = ({ orgId, locations, onLocationsChange }) => {
  const list = (locations || []).filter(l => l.active !== false && !l.is_personal);
  const onSaved = (id, positions) => onLocationsChange((locations || []).map(l => (l.id === id ? { ...l, shooting_positions: positions } : l)));
  return (
    <div className="p-4 bg-gray-700/30 rounded-lg border border-gray-600 space-y-2" data-testid="settings-capacity-locations">
      <p className="font-medium text-white text-sm">Baner med kapasitetsstyring</p>
      <p className="text-xs text-gray-400">Huk av banene som skal ha lag, og skriv inn antall standplasser (= plasser per lag). Lagres med en gang. QR-kodene påvirkes ikke.</p>
      {list.length === 0 && <p className="text-sm text-gray-400">Ingen skytebaner er lagt inn ennå.</p>}
      {list.map(l => <Row key={l.id} orgId={orgId} loc={l} onSaved={onSaved} />)}
    </div>
  );
};
